import { useState, type FormEvent } from 'react';
import { NavLink, useSearchParams } from 'react-router';
import type { List } from '@cleartask/shared';
import { ApiClientError } from '../../lib/api.js';
import { ColorField } from '../../components/ui/ColorField.js';
import { ConfirmDialog } from '../../components/ui/ConfirmDialog.js';
import { useToast } from '../../components/ui/Toast.js';
import { newListId, useCreateList, useDeleteList, useLists, useUpdateList } from './useLists.js';

/**
 * Listas do usuario, mais as que a equipe compartilhou (spec 9.6).
 *
 * Cada item e um LINK para `/tarefas?lista=<id>`. Ate a fase 9 eram `<div>`
 * inertes: dava para criar uma lista, ela aparecia na barra lateral e clicar
 * nela nao fazia nada. A infraestrutura de filtro existia inteira — o schema
 * aceitava `listId`, o cliente montava a query, a chave de cache ja o incluia —
 * e nenhuma tela passava o parametro.
 *
 * Renomear e excluir chegaram depois, pela MESMA razao: `useUpdateList` e
 * `useDeleteList` existiam desde a fase 2 e nenhum componente os chamava. Uma
 * lista criada com o nome errado ficava errada para sempre, e uma lista que
 * sobrou nao tinha como sair da barra lateral.
 *
 * O componente e compartilhado entre a barra lateral (desktop) e a aba Mais
 * (mobile), porque a barra some abaixo de 768px: sem a segunda montagem, quem
 * usa o celular nao tem acesso nenhum a listas.
 */
export function ListsPanel() {
  const { data, isPending } = useLists();
  const createList = useCreateList();
  const [params] = useSearchParams();

  const [name, setName] = useState('');
  const [adding, setAdding] = useState(false);
  const [aExcluir, setAExcluir] = useState<List | null>(null);

  const listaAtiva = params.get('lista');

  function submit(event: FormEvent) {
    event.preventDefault();

    const trimmed = name.trim();
    if (trimmed === '') return;

    createList.mutate({ id: newListId(), name: trimmed });
    setName('');
    setAdding(false);
  }

  const lists = data?.lists ?? [];

  return (
    <div className="flex flex-col gap-1">
      {isPending ? <p className="px-3 text-sm text-text-mut">Carregando…</p> : null}

      {!isPending && lists.length === 0 ? (
        <p className="px-3 pb-1 text-sm text-text-mut">
          Nenhuma lista ainda. Crie uma para agrupar tarefas por assunto.
        </p>
      ) : null}

      {lists.map((list) => (
        <ItemDaLista
          key={list.id}
          list={list}
          ativa={list.id === listaAtiva}
          onExcluir={() => setAExcluir(list)}
        />
      ))}

      {adding ? (
        <form onSubmit={submit} className="px-2 pt-1">
          <label className="sr-only" htmlFor="new-list">
            Nome da lista
          </label>
          <input
            id="new-list"
            value={name}
            onChange={(event) => setName(event.target.value)}
            onBlur={() => {
              if (name.trim() === '') setAdding(false);
            }}
            placeholder="Nome da lista"
            className="min-h-touch w-full rounded-sm border border-border bg-surface px-2 text-sm"
          />
        </form>
      ) : (
        <button
          type="button"
          onClick={() => setAdding(true)}
          className="min-h-touch rounded-sm px-3 text-left text-sm text-brand-blue hover:bg-surface-2"
        >
          + Nova lista
        </button>
      )}

      <DialogoDeExclusao lista={aExcluir} onFechar={() => setAExcluir(null)} />
    </div>
  );
}

/**
 * Uma linha da barra: link de filtro, renomear e excluir.
 *
 * Os dois botoes ficam SEMPRE no DOM, e nao aparecem no hover: revela-los ao
 * passar o mouse os tornaria inalcancaveis por toque e por teclado (spec 10.5),
 * e a barra e justamente o que o mobile acessa pela aba Mais.
 */
function ItemDaLista({
  list,
  ativa,
  onExcluir,
}: {
  list: List;
  ativa: boolean;
  onExcluir: () => void;
}) {
  const updateList = useUpdateList();
  const { showToast } = useToast();

  const [editando, setEditando] = useState(false);
  const [nome, setNome] = useState(list.name);
  const [cor, setCor] = useState(list.color);

  function salvar(event: FormEvent) {
    event.preventDefault();

    const limpo = nome.trim();
    const nomeMudou = limpo !== '' && limpo !== list.name;
    const corMudou = cor !== list.color;

    // Nada mudou nao vira requisicao: gravar o mesmo valor gastaria uma linha
    // de `audit_log` afirmando uma alteracao que nao houve.
    if (!nomeMudou && !corMudou) {
      setNome(list.name);
      setCor(list.color);
      setEditando(false);
      return;
    }

    updateList.mutate(
      {
        id: list.id,
        patch: { ...(nomeMudou ? { name: limpo } : {}), ...(corMudou ? { color: cor } : {}) },
      },
      {
        onSuccess: () => {
          showToast({ message: nomeMudou ? 'Lista renomeada' : 'Cor da lista salva' });
          setEditando(false);
        },
      },
    );
  }

  const erro = updateList.error instanceof ApiClientError ? updateList.error.message : null;

  if (editando) {
    return (
      <form onSubmit={salvar} className="px-2 py-0.5">
        <div className="flex items-center gap-1">
          <label className="sr-only" htmlFor={`rename-list-${list.id}`}>
            Novo nome da lista {list.name}
          </label>
          <input
            id={`rename-list-${list.id}`}
            autoFocus
            value={nome}
            onChange={(event) => setNome(event.target.value)}
            onKeyDown={(event) => {
              if (event.key === 'Escape') {
                setNome(list.name);
                setCor(list.color);
                setEditando(false);
              }
            }}
            className="min-h-touch w-full rounded-sm border border-border bg-surface px-2 text-sm"
          />

          <ColorField
            id={`color-list-${list.id}`}
            label={`Cor da lista ${list.name}`}
            value={cor}
            onChange={setCor}
          />

          {/*
            Com dois campos e nenhum botao de submit, o HTML desliga a
            submissao implicita por Enter — o formulario ficaria sem NENHUM
            jeito de salvar por toque, que e o acesso do mobile (spec 10.5).
          */}
          <button
            type="submit"
            aria-label={`Salvar a lista ${list.name}`}
            className="min-h-touch shrink-0 rounded-sm px-2 text-sm text-brand-blue hover:bg-surface-2"
          >
            ✓
          </button>
        </div>
        {erro ? (
          <p role="alert" className="pt-1 text-sm text-overdue">
            {erro}
          </p>
        ) : null}
      </form>
    );
  }

  return (
    <div className="flex items-center">
      <NavLink
        to={`/tarefas?lista=${list.id}`}
        className={[
          'flex min-h-touch flex-1 items-center gap-2 rounded-sm px-3 text-sm transition-colors duration-150',
          ativa ? 'bg-brand-blue-soft font-medium text-brand-blue' : 'hover:bg-surface-2',
        ].join(' ')}
      >
        <span
          aria-hidden="true"
          className="h-2 w-2 shrink-0 rounded-full"
          style={{ backgroundColor: list.color }}
        />
        <span className="truncate">{list.name}</span>
        {list.shared ? (
          <span className="ml-auto text-xs text-text-mut" title="Compartilhada">
            equipe
          </span>
        ) : null}
      </NavLink>

      <button
        type="button"
        onClick={() => setEditando(true)}
        aria-label={`Renomear a lista ${list.name}`}
        className="min-h-touch shrink-0 rounded-sm px-2 text-sm text-text-mut hover:bg-surface-2 hover:text-text"
      >
        ✎
      </button>

      <button
        type="button"
        onClick={onExcluir}
        aria-label={`Excluir a lista ${list.name}`}
        className="min-h-touch shrink-0 rounded-sm px-2 text-sm text-text-mut hover:bg-surface-2 hover:text-overdue"
      >
        ✕
      </button>
    </div>
  );
}

/**
 * Confirmacao de exclusao.
 *
 * Vive fora do item para nao remontar junto com a linha e para haver UM
 * dialogo por painel, e nao um por lista.
 *
 * `DELETE /lists/:id` **recusa lista nao vazia com 409** — a FK e
 * `ON DELETE SET NULL`, entao apagar funcionaria e soltaria as tarefas no "sem
 * lista" sem ninguem ter pedido. A mensagem do servidor e mostrada como toast
 * de erro: sem ela, o clique em "Excluir" simplesmente nao faria nada visivel.
 */
function DialogoDeExclusao({ lista, onFechar }: { lista: List | null; onFechar: () => void }) {
  const deleteList = useDeleteList();
  const { showToast } = useToast();

  if (!lista) return null;

  return (
    <ConfirmDialog
      open
      title={`Excluir a lista “${lista.name}”?`}
      message="As tarefas não são apagadas — a lista precisa estar vazia. Não há como desfazer."
      confirmLabel="Excluir"
      onCancel={onFechar}
      onConfirm={() => {
        deleteList.mutate(lista.id, {
          onSuccess: () => showToast({ message: 'Lista excluída' }),
          onError: (error) => {
            showToast({
              message:
                error instanceof ApiClientError
                  ? error.message
                  : 'Não foi possível excluir a lista.',
            });
          },
        });
        onFechar();
      }}
    />
  );
}
